import baseApi from './RootBaseApi';
import type {
  ActorType,
  CommissionPolicy,
  CommissionPolicyCreatePayload,
  CommissionPolicyUpdatePayload,
} from '@/types/commission-policy.types';

export type CommissionPolicyActorPayload = Pick<
  CommissionPolicyCreatePayload,
  'actor_type' | 'actor_id'
>;

export type CommissionPolicyUpdateWithActorPayload =
  CommissionPolicyUpdatePayload & Partial<CommissionPolicyActorPayload>;

function hasData<T>(res: unknown): res is { data: T } {
  return (
    typeof res === 'object' &&
    res !== null &&
    'data' in (res as Record<string, unknown>)
  );
}

export type GetCommissionPolicyByActorParams = {
  actor_id: string | null;
  actor_type: ActorType;
};

export const CommissionPolicyService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getGlobalCommissionPolicy: builder.query<CommissionPolicy | null, void>({
      query: () => ({
        url: '/commission-policies/global',
        method: 'GET',
      }),
      transformResponse: (
        response: { data: CommissionPolicy | null } | CommissionPolicy | null
      ) => {
        if (response === null) return null;
        return hasData<CommissionPolicy | null>(response)
          ? response.data
          : (response as CommissionPolicy);
      },
      providesTags: ['CommissionPolicies'],
    }),

    getCommissionPolicyByActor: builder.query<
      CommissionPolicy | null,
      GetCommissionPolicyByActorParams
    >({
      query: ({ actor_id, actor_type }) => ({
        url: '/commission-policies',
        method: 'GET',
        params: { actor_id, actor_type },
      }),
      transformResponse: (
        response: { data: CommissionPolicy | null } | CommissionPolicy | null
      ) => {
        if (response === null) return null;
        return hasData<CommissionPolicy | null>(response)
          ? response.data
          : (response as CommissionPolicy);
      },
      providesTags: ['CommissionPolicies'],
    }),

    createCommissionPolicy: builder.mutation<
      CommissionPolicy,
      CommissionPolicyCreatePayload
    >({
      query: (payload) => ({
        url: '/commission-policies',
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: ['CommissionPolicies'],
    }),

    createGlobalCommissionPolicy: builder.mutation<
      CommissionPolicy,
      Omit<CommissionPolicyCreatePayload, 'actor_type' | 'actor_id'>
    >({
      query: (payload) => ({
        url: '/commission-policies/global',
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: ['CommissionPolicies'],
    }),

    updateCommissionPolicy: builder.mutation<
      CommissionPolicy,
      { id: string; payload: CommissionPolicyUpdateWithActorPayload }
    >({
      query: ({ id, payload }) => ({
        url: `/commission-policies/${id}`,
        method: 'PUT',
        data: payload,
      }),
      invalidatesTags: ['CommissionPolicies'],
    }),

    deactivateCommissionPolicy: builder.mutation<void, string>({
      query: (id) => ({
        url: `/commission-policies/${id}/deactivate`,
        method: 'POST',
      }),
      invalidatesTags: ['CommissionPolicies'],
    }),
  }),
});

export const {
  useGetGlobalCommissionPolicyQuery,
  useGetCommissionPolicyByActorQuery,
  useCreateCommissionPolicyMutation,
  useCreateGlobalCommissionPolicyMutation,
  useUpdateCommissionPolicyMutation,
  useDeactivateCommissionPolicyMutation,
} = CommissionPolicyService;
